import React from 'react';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { useApplyTemplatesInBulk } from '@/lib/api/_hooks/template/use-apply-templates-in-bulk';
import { useGetTemplates } from '@/lib/api/_hooks/template/use-get-templates';
import { useShallowSelector } from '@/lib/reduxHooks';
import * as actions from '@/store/applyTemplate/actions';
import { errorToast } from '@/store/pageFrame/actions';
import type { ITemplateMetadata } from '@/store/templateManagement/types';
import { applyTemplateToDocumentId } from '../../store/senderBuild/actions';
import { TemplatesList } from '../applyTemplate/templates-list';
import { LoaderButton } from '../common';
import { BuilderShelfWrapper } from './builder-shelf-wrapper';

interface TemplatePickerShelfDrawerProps extends React.ComponentProps<'div'> {
  show: boolean;
  close: () => void;
  envelopeId: string;
}

export function TemplatePickerShelfDrawer(props: TemplatePickerShelfDrawerProps) {
  const { className, show, close, envelopeId, ...rest } = props;
  const dispatch = useDispatch();

  const { applyTemplateToDocument } = useShallowSelector((state) => ({
    applyTemplateToDocument: state.senderBuild.documents.find(
      (doc) => doc.documentId === state.senderBuild.applyTemplateToDocumentId
    ),
  }));

  const [selectedTemplate, setSelectedTemplate] = React.useState<ITemplateMetadata | null>(null);
  // const [isMismatchedTemplateDialogOpen, setIsMismatchedTemplateDialogOpen] = React.useState(false);
  // const [missingRecipientTitles, setMissingRecipientTitles] = React.useState<string[] | null>(null);

  const getAllTemplates = useGetTemplates();
  // const getSingleTemplate = useGetTemplate({ templateId: selectedTemplate?.id });
  const applyTemplates = useApplyTemplatesInBulk(envelopeId);

  function handleClose() {
    setSelectedTemplate(null);
    dispatch(actions.previewTemplate(undefined));
    dispatch(applyTemplateToDocumentId(null));
    close();
  }

  function handleSelectTemplate(template: ITemplateMetadata, wasJustSelected: boolean) {
    if (wasJustSelected) {
      setSelectedTemplate(template);
      dispatch(actions.previewTemplate(template.id));
    } else {
      setSelectedTemplate(null);
      dispatch(actions.previewTemplate(undefined));
    }
  }

  // Because the template service now maps Forms contact types to Digisign template titles
  // (AP-2386), this simple logic where we just check for missing titles is no longer sufficient.
  // We will either need to copy the logic from the template service here, or
  // have the template service return more detailed info about which recipients are missing.
  //
  // async function findMissingRecipientTitles() {
  //   let { recipients: templateRecipients } = await getSingleTemplate.ensureQueryData();
  //   let envelopeRecipientTitles = recipients.map((r) => r.title).filter(Boolean) as string[];
  //
  //   let missingRecipientTitles = [];
  //   for (const r of templateRecipients) {
  //     if (!envelopeRecipientTitles.includes(r.recipientTitle)) {
  //       missingRecipientTitles.push(r.recipientTitle);
  //     }
  //   }
  //
  //   // setMissingRecipientTitles(missingRecipientTitles);
  //   return missingRecipientTitles;
  // }
  // async function checkCanApplyTemplate() {
  //   if (!selectedTemplate || !applyTemplateToDocument) return false;
  //   let missingRecipients = await findMissingRecipientTitles();
  //   return missingRecipients.length === 0;
  // }

  async function handleApplyTemplate() {
    if (!selectedTemplate || !applyTemplateToDocument) return;

    try {
      await applyTemplates.mutateAsync([
        {
          templateId: selectedTemplate.id,
          envelopeDocumentId: applyTemplateToDocument.documentId,
        },
      ]);

      handleClose();
    } catch (e: any) {
      const msg = e?.message || 'Error applying template';
      dispatch(errorToast(msg));
    }
  }

  return (
    <BuilderShelfWrapper
      {...rest}
      show={show}
      side="right"
      className={twMerge(
        'bg-grey-50 border-l border-solid border-grey-300',
        'flex flex-col',
        'max-md:w-full',
        className
      )}
    >
      <div className="border-b border-solid border-grey-300">
        <h2 className="text-xs uppercase font-bold px-4 tracking-wide text-grey-800 m-0 py-4">Templates</h2>
      </div>

      <TemplatesList
        templates={getAllTemplates.data}
        isLoadingTemplates={getAllTemplates.isLoading}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={handleSelectTemplate}
      />

      <div className="flex flex-col gap-4 border-t border-solid border-grey-300 px-6 py-4">
        <p className="text-right select-none flex flex-col gap-1">
          <span className="text-grey-700">Choose a template to apply to</span>{' '}
          <strong className="font-bold truncate">{applyTemplateToDocument?.fileName || 'the document'}</strong>
        </p>

        <div className="flex gap-4 justify-end ">
          <Button color="primary" variant="text" onClick={handleClose} className="py-3 px-6">
            Cancel
          </Button>
          <LoaderButton
            color="primary"
            variant="contained"
            disabled={!selectedTemplate || !applyTemplateToDocument}
            onClickAsync={async () => {
              await handleApplyTemplate();
              // if (await checkCanApplyTemplate()) {
              //   await handleApplyTemplate();
              // } else {
              //   setIsMismatchedTemplateDialogOpen(true);
              // }
            }}
          >
            Apply
          </LoaderButton>
        </div>
      </div>

      {/* <Modal
        open={isMismatchedTemplateDialogOpen}
        title="Some roles are not matching."
        onClose={() => setIsMismatchedTemplateDialogOpen(false)}
        showCancelButton
        actionButtons={
          <LoaderButton color="primary" variant="contained" onClickAsync={handleApplyTemplate}>
            Apply
          </LoaderButton>
        }
      >
        <Typography>
          The template you selected includes roles that are not currently assigned to recipients in your envelope.
          Fields for these roles will not be applied:
        </Typography>

        <ul className="list-none mt-6">
          {missingRecipientTitles?.map((title) => (
            <li key={title} className="flex items-center gap-2 font-bold">
              <Close className="text-red-500" />
              {title}
            </li>
          ))}
        </ul>
      </Modal> */}
    </BuilderShelfWrapper>
  );
}
